import { z } from 'zod';
import { NewPassword } from './auth.js';
import { HEARD_FROMS, LIVING_SITUATIONS, PROVINCES } from './enums.js';
import { Email, IsoDateTime, MemberNumber, optional, PersonName, PolicyVersion, PostalCode, ReferralCode, ZaPhone, } from './primitives.js';
const AddressLine = z
    .string()
    .trim()
    .min(1, 'Enter your street address.')
    .max(120, 'Keep this under 120 characters.');
/** Up to this many saved addresses per member (D25). */
export const MAX_MEMBER_ADDRESSES = 5;
/**
 * Where a member lives: the sign-up address, and each address saved later.
 * `areaId` is a suburb from `GET /catalog/areas`, or null for "My suburb isn't
 * listed" (D24). With no area, the member types their suburb.
 */
const AddressFields = {
    addressLine1: AddressLine,
    addressLine2: optional(z.string().trim().max(120, 'Keep this under 120 characters.')),
    province: z.enum(PROVINCES, { error: 'Choose your province.' }),
    areaId: z.cuid2({ error: 'Choose your suburb, or pick "My suburb isn\'t listed".' }).nullable(),
    suburb: optional(z.string().trim().max(80, 'Keep this under 80 characters.')),
    postalCode: optional(PostalCode),
};
/** Who the member is: the part of sign-up a member can change later, apart from addresses. */
const MemberDetailsFields = {
    firstName: PersonName,
    lastName: PersonName,
    phone: ZaPhone,
    /** One answer per member, not per address (D25). */
    livingSituation: z.enum(LIVING_SITUATIONS, { error: 'Tell us whether you own or rent your home.' }),
    /** Unticked by default; every change is stored as a new consent with a timestamp (POPIA). */
    marketingConsent: z.boolean(),
};
/** With no area picked, the member must type their suburb. */
function hasSuburb(value) {
    return value.areaId !== null || (value.suburb ?? '') !== '';
}
const SUBURB_REQUIRED = { path: ['suburb'], message: 'Tell us which suburb or town you live in.' };
/**
 * Every field `POST /members/register` takes, without the cross-field suburb
 * rule. Exported unrefined so a form split over several steps can
 * `.pick()` the fields of one step and check them on their own: Zod refuses
 * `.pick()` on a schema that carries refinements. Validate the whole body
 * with `RegisterMemberInput`, not this.
 */
export const RegisterMemberFields = z.object({
    ...MemberDetailsFields,
    ...AddressFields,
    email: Email,
    password: NewPassword,
    heardFrom: z.enum(HEARD_FROMS, { error: 'Tell us where you heard about MyHome+.' }),
    referralCode: optional(ReferralCode),
    acceptPolicies: z.literal(true, {
        error: 'You need to accept the terms and conditions and the privacy policy to join.',
    }),
    termsVersion: PolicyVersion,
    privacyVersion: PolicyVersion,
});
/**
 * The suburb rule on its own, so a step that holds the address fields can
 * apply the same check the whole body does.
 */
export const requireSuburb = { check: hasSuburb, issue: SUBURB_REQUIRED };
/**
 * `POST /members/register` (requirements.md, Member registration; auth.md,
 * Member registration).
 */
export const RegisterMemberInput = RegisterMemberFields.refine(hasSuburb, SUBURB_REQUIRED);
/**
 * `PATCH /members/me`: the member changes their own details. Addresses have
 * their own endpoints. Email (Better Auth's confirmed change), where they
 * heard about us, the referral code (set once, domain.md) and the accepted
 * policy versions are not part of it.
 */
export const UpdateMemberProfileInput = z.object(MemberDetailsFields);
/**
 * `POST /members/me/addresses` and `PATCH /members/me/addresses/{id}`. The
 * address registration created is labelled "Home".
 */
export const MemberAddressInput = z
    .object({
    label: z
        .string()
        .trim()
        .min(1, 'Give this address a name, like Home or Rental flat.')
        .max(40, 'Keep the name under 40 characters.'),
    ...AddressFields,
})
    .refine(hasSuburb, SUBURB_REQUIRED);
/** A saved address. Exactly one per member is primary: directory search starts there (D25). */
export const MemberAddress = z.object({
    id: z.string(),
    label: z.string(),
    line1: z.string(),
    line2: z.string().nullable(),
    suburb: z.string().nullable(),
    city: z.string().nullable(),
    postalCode: z.string().nullable(),
    province: z.enum(PROVINCES),
    /** The picked suburb, or null for "My suburb isn't listed". */
    areaId: z.string().nullable(),
    isPrimary: z.boolean(),
});
/**
 * 201 from `POST /members/register`, with the session cookie set. When
 * `inServedArea` is false the screen says providers are not available there
 * yet (D3); competitions and rewards still apply.
 */
export const RegisterMemberResult = z.object({
    memberNumber: MemberNumber,
    inServedArea: z.boolean(),
});
/**
 * 200 from `GET /members/me`: the member's details and saved addresses, for
 * the Account screen. `phone` is E.164; `memberSince` is an ISO date-time in UTC.
 */
export const MemberProfile = z.object({
    memberNumber: MemberNumber,
    memberSince: IsoDateTime,
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    emailVerified: z.boolean(),
    phone: z.string(),
    /** The primary address first, then the rest in the order they were added. */
    addresses: z.array(MemberAddress).min(1),
    livingSituation: z.enum(LIVING_SITUATIONS),
    heardFrom: z.enum(HEARD_FROMS),
    referralCode: z.string().nullable(),
    /** The latest marketing choice. Terms and privacy are always accepted, at these versions. */
    consents: z.object({
        marketing: z.boolean(),
        termsVersion: z.string(),
        privacyVersion: z.string(),
    }),
});
//# sourceMappingURL=members.js.map